import React, { useEffect, useState } from 'react';
import { CloseIcon } from '../icons';
import { apiService } from '../../services/apiService';
import { trackClick } from '../../utils/analytics';

/**
 * "Have our concierge approach them for me."
 *
 * This is a consent screen before an internal hand-off, not a message
 * composer. It says plainly what we will pass on, lets the user correct the
 * name and email we hold, and lets them copy colleagues on their side. The
 * request goes to the concierge inbox; the user is not a recipient, because a
 * human replies to them separately.
 */

interface ConciergeRequestModalProps {
    isOpen: boolean;
    onClose: () => void;
    techId: string;
    techTitle: string;
    university?: string;
    defaultName?: string;
    defaultEmail?: string;
}

const MAX_CC = 5;

export const ConciergeRequestModal: React.FC<ConciergeRequestModalProps> = ({
    isOpen, onClose, techId, techTitle, university, defaultName, defaultEmail,
}) => {
    const [name, setName] = useState(defaultName || '');
    const [email, setEmail] = useState(defaultEmail || '');
    const [cc, setCc] = useState<string[]>([]);
    const [ccDraft, setCcDraft] = useState('');
    const [note, setNote] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [done, setDone] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        setName(defaultName || '');
        setEmail(defaultEmail || '');
        setCc([]); setCcDraft(''); setNote('');
        setError(''); setBusy(false); setDone(false);
    }, [isOpen, defaultName, defaultEmail]);

    if (!isOpen) return null;

    const valid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
    const addCc = () => {
        const e = ccDraft.trim();
        if (!e) return;
        if (!valid.test(e)) { setError('That does not look like an email address.'); return; }
        if (cc.length >= MAX_CC) { setError(`You can copy up to ${MAX_CC} people.`); return; }
        if (cc.some(x => x.toLowerCase() === e.toLowerCase())) { setCcDraft(''); return; }
        setCc(list => [...list, e]); setCcDraft(''); setError('');
    };

    const submit = async () => {
        if (!name.trim()) { setError('Tell us what name to use.'); return; }
        if (!valid.test(email.trim())) { setError('Enter an email we can reply to.'); return; }
        setBusy(true); setError('');
        trackClick('concierge_submit_request', { tech_id: techId, cc_count: cc.length });
        const res = await apiService.submitConciergeRequest({
            tech_id: techId, tech_title: techTitle, university: university || '',
            contact_name: name.trim(), contact_email: email.trim(),
            cc_emails: cc, note: note.trim(),
        });
        setBusy(false);
        if (res.success && (res.data as any)?.success) setDone(true);
        else setError((res.data as any)?.error || res.error
            || 'Could not send that just now. Please try again.');
    };

    return (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex justify-center items-center z-[60] p-4"
             onClick={onClose}>
            <div className="relative bg-white rounded-2xl shadow-lg w-full max-w-lg p-6 flex flex-col gap-4 max-h-[90vh] overflow-y-auto"
                 onClick={e => e.stopPropagation()}>
                <button type="button" onClick={onClose}
                    className="absolute top-4 right-4 flex p-2.5 justify-center items-center rounded-full bg-grayscale-1200 shadow-custom-light">
                    <CloseIcon />
                </button>

                {done ? (
                    <>
                        <h2 className="text-grayscale-1300 font-grotesk text-base font-medium pr-10">
                            Request sent
                        </h2>
                        <p className="text-grayscale-1200 font-grotesk text-sm">
                            Our concierge has the details and will approach the Tech Transfer
                            Office. They will come back to you at <b>{email}</b>
                            {cc.length ? <>, copying {cc.join(', ')}</> : null}.
                        </p>
                        <button onClick={onClose}
                            className="self-end py-2.5 px-5 rounded-full bg-primary-500 text-grayscale-100 font-grotesk text-sm font-medium">
                            Done
                        </button>
                    </>
                ) : (
                    <>
                        <div className="pr-10">
                            <h2 className="text-grayscale-1300 font-grotesk text-base font-medium">
                                Ask our concierge to reach out
                            </h2>
                            <p className="text-grayscale-1100 font-grotesk text-xs mt-1">
                                We will contact the Tech Transfer Office for you. Nothing is sent
                                until you confirm below.
                            </p>
                        </div>

                        {/* Exactly what leaves the building, stated plainly. */}
                        <div className="rounded-lg bg-alternate-bg p-3 text-xs text-grayscale-1200 flex flex-col gap-1">
                            <span className="font-medium text-grayscale-1300">What we will share</span>
                            <span>• The technology you selected{techTitle ? `: ${techTitle}` : ''}
                                {university ? ` (${university})` : ''}</span>
                            <span>• Your name and email below, so they can reply</span>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-3">
                            <label className="flex-1 flex flex-col gap-1">
                                <span className="text-grayscale-1100 font-grotesk text-xs">Your name</span>
                                <input value={name} onChange={e => setName(e.target.value)} disabled={busy}
                                    className="h-11 px-2.5 rounded-md bg-grayscale-200 text-sm font-grotesk focus:outline-none" />
                            </label>
                            <label className="flex-1 flex flex-col gap-1">
                                <span className="text-grayscale-1100 font-grotesk text-xs">Reply-to email</span>
                                <input value={email} onChange={e => setEmail(e.target.value)} disabled={busy}
                                    type="email" autoComplete="email"
                                    className="h-11 px-2.5 rounded-md bg-grayscale-200 text-sm font-grotesk focus:outline-none" />
                            </label>
                        </div>

                        <div className="flex flex-col gap-1">
                            <span className="text-grayscale-1100 font-grotesk text-xs">
                                Copy your colleagues (optional, up to {MAX_CC})
                            </span>
                            <div className="flex gap-2">
                                <input value={ccDraft} onChange={e => setCcDraft(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCc(); } }}
                                    placeholder="colleague@company.com" disabled={busy}
                                    className="flex-1 h-11 px-2.5 rounded-md bg-grayscale-200 text-sm font-grotesk focus:outline-none" />
                                <button type="button" onClick={addCc} disabled={busy}
                                    className="px-4 rounded-md border border-primary-300 text-primary-700 font-grotesk text-sm">
                                    Add
                                </button>
                            </div>
                            {cc.length > 0 && (
                                <div className="flex flex-wrap gap-1.5 mt-1">
                                    {cc.map(e => (
                                        <span key={e}
                                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 text-xs">
                                            {e}
                                            <button type="button" aria-label={`Remove ${e}`}
                                                onClick={() => setCc(l => l.filter(x => x !== e))}>×</button>
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>

                        <label className="flex flex-col gap-1">
                            <span className="text-grayscale-1100 font-grotesk text-xs">
                                Anything they should know? (optional)
                            </span>
                            <textarea value={note} onChange={e => setNote(e.target.value)} rows={3}
                                maxLength={2000} disabled={busy}
                                placeholder="What you are hoping to do with this technology…"
                                className="p-2.5 rounded-md bg-grayscale-200 text-sm font-grotesk resize-none focus:outline-none" />
                        </label>

                        {error && <p className="text-red-600 font-grotesk text-sm">{error}</p>}

                        <div className="flex items-center gap-3 justify-end">
                            <button type="button" onClick={onClose} disabled={busy}
                                className="py-2.5 px-4 text-grayscale-1200 font-grotesk text-sm">
                                Cancel
                            </button>
                            <button type="button" onClick={submit} disabled={busy}
                                className="py-2.5 px-5 rounded-full bg-primary-500 text-grayscale-100 font-grotesk text-sm font-medium disabled:opacity-60">
                                {busy ? 'Sending…' : 'Share and request contact'}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default ConciergeRequestModal;
